import React, { useRef, useState, useEffect } from "react";
import { GeoJSON } from "react-leaflet";
import { useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { FormGroup, Label, Input, Spinner } from "reactstrap";
import L from "leaflet";
import PropTypes from "prop-types";
import PerfectScrollbar from "react-perfect-scrollbar";
import { setBidangResult, setLegendNow } from "~/redux/actions";
import {
  ucFirst,
  renderToFeaturesAttribution,
  GEOJSON_STYLES_WITH_LAYER_BIDANG,
  checkHollowMaps,
  DEFAULT_GEOJSON_STYLES,
} from "~/utils";
import LayoutPeta from "~/layouts/peta";
import MapsCore from "~/components/MapsCore";
import TabPeta from "./peta-bidang/tabs-peta";
import request from "~/utils/request";
import LoadingGlobal from "~/components/LoadingGlobal";

const PetaWithLayerBidang = () => {
  const { search } = useLocation();
  const { desa } = useSelector((state) => state.Auth);
  const { hollowMaps, sigTipeData } = useSelector((state) => state.Map);

  const mapRef = useRef(null);
  const layerRef = useRef(null);
  const dispatch = useDispatch();

  const [infoLayer, setInfoLayer] = useState(null);
  const [geoJSONData, setGeoJSONData] = useState(null);
  const [activeLayer, setActiveLayer] = useState(null);
  const [bidangSummary, setBidangSummary] = useState(null);
  const [loadingInfoLayer, setLoadingInfoLayer] = useState(false);
  const [loadingGeoJSONData, setLoadingGeoJSONData] = useState(false);

  const tipeQuerySearch = new URLSearchParams(search).get("tipe");

  const handleClickLayer = (feature, layer) => {
    window.layer = layer;

    // CHECKING TYPE GEOJSON
    if (layer?.feature?.geometry?.type === "Point") {
      mapRef.current.leafletElement.flyTo(
        new L.LatLng(layer?._latlng.lat, layer?._latlng.lng),
        desa?.zoom + 2
      );
      layer.bindTooltip(layer.feature.properties.properties.nama).openTooltip();
    } else {
      const centroid = layer.getBounds().getCenter();
      mapRef.current.leafletElement.flyTo(
        new L.LatLng(centroid.lat, centroid.lng),
        desa?.zoom + 2
      );
      layerRef.current.leafletElement.resetStyle();
      setTimeout(() => {
        layer.setStyle({
          color: "#FFF",
          weight: 7,
          opacity: 0.8,
          fillOpacity: 0.8,
        });
      }, 1);
    }

    setActiveLayer(layer);
  };

  const getBidangSummary = async () => {
    try {
      const response = await request.get(
        `/v1/sigbidang-summary?tipe=${tipeQuerySearch}`
      );
      const datas = response.data.data;

      setBidangSummary(datas);
    } finally {
    }
  };

  const handleEachFeature = (feature, layer) => {
    layer.on({
      mouseover: (e) => {
        if (layer) {
          let layer = e.target;

          const tooltipNBT = `NBT ${feature?.properties?.properties?.nama}`;

          const tooltipMapWithBidang = `Jumlah Penerima ${ucFirst(
            tipeQuerySearch
          )} ${bidangSummary?.bidangdata?.[feature?.properties?.id] ?? 0}`;

          const tooltipMapWithBidangInDesa = `Jumlah Penerima ${ucFirst(
            tipeQuerySearch
          )} di Desa ${bidangSummary?.jumlah}`;

          const tooltipWithLayerBidang = `${tooltipNBT} <br /> ${tooltipMapWithBidang} <br /> ${tooltipMapWithBidangInDesa}`;

          layer.bindTooltip(tooltipWithLayerBidang);
        }
      },
      click: (e) => {
        let layer = e.target;
        handleClickLayer(feature, layer);

        setLoadingInfoLayer(true);

        request
          .get(`/v1/sig/${layer?.feature?.properties?.id}/detailinfo`)
          .then((res) => setInfoLayer(res.data.data))
          .finally(() => setLoadingInfoLayer(false));
      },
    });
  };

  const fetchStreamBidang = async (uri) => {
    setLoadingGeoJSONData(true);
    try {
      const datas = await fetch(uri);
      const results = await datas.json();
      const features = await renderToFeaturesAttribution(results);

      const featureColection = {
        type: "FeatureCollection",
        features: features,
      };

      setGeoJSONData(featureColection);
      dispatch(setBidangResult(featureColection.features.length));
      await getBidangSummary();
    } finally {
      setLoadingGeoJSONData(false);
    }
  };

  const createFeatureStyle = (feature) => {
    const findBidangSummaryDataInClasification =
      sigTipeData?.klasifikasi?.klasifikasi_atribut?.find(
        (classification) =>
          classification.nama >=
          bidangSummary?.bidangdata?.[feature?.properties?.id]
      );

    if (
      typeof bidangSummary?.bidangdata?.[feature?.properties?.id] === "number"
    ) {
      if (typeof findBidangSummaryDataInClasification !== "undefined") {
        return checkHollowMaps(hollowMaps, {
          ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
          fillColor: findBidangSummaryDataInClasification?.fillColor,
        });
      }

      return checkHollowMaps(hollowMaps, {
        ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
        fillColor: GEOJSON_STYLES_WITH_LAYER_BIDANG.ACTIVE.fillColor,
      });
    }

    return checkHollowMaps(hollowMaps, {
      ...DEFAULT_GEOJSON_STYLES.OTHER_THAN_POINT_NO_FILLCOLOR,
      fillColor: GEOJSON_STYLES_WITH_LAYER_BIDANG.INACTIVE.fillColor,
    });
  };

  useEffect(() => {
    setLoadingGeoJSONData(true);
    request
      .get("/v1/sig/stream?filter{tipe.nama}=bidang")
      .then(async (response) => {
        await fetchStreamBidang(
          `${process.env.REACT_APP_API_URL}${response.data.data.url}`
        );
      })
      .finally(() => setLoadingGeoJSONData(false));
  }, []);

  useEffect(() => {
    if (sigTipeData && bidangSummary && geoJSONData) {
      geoJSONData.features.map((feature) => {
        const findBidangSummaryDataInClasification =
          sigTipeData?.klasifikasi?.klasifikasi_atribut?.find(
            (classification) =>
              classification.nama ===
              bidangSummary?.bidangdata?.[feature?.properties?.id]
          );

        if (
          typeof bidangSummary?.bidangdata?.[feature?.properties?.id] ===
          "number"
        ) {
          if (typeof findBidangSummaryDataInClasification !== "undefined") {
            return dispatch(
              setLegendNow({
                warna: findBidangSummaryDataInClasification.fillColor,
                ket_color: `${sigTipeData?.nama_satuan ?? ""}`,
              })
            );
          }

          return dispatch(
            setLegendNow({
              ket_color: `${sigTipeData?.nama_satuan} (tidak terdaftar di klasifikasi)`,
              warna: GEOJSON_STYLES_WITH_LAYER_BIDANG.ACTIVE.fillColor,
            })
          );
        }

        return dispatch(
          setLegendNow({
            ket_color: `Bukan ${sigTipeData?.nama_satuan ?? ""}`,
            warna: GEOJSON_STYLES_WITH_LAYER_BIDANG.INACTIVE.fillColor,
          })
        );
      });
    }
  }, [sigTipeData, bidangSummary, geoJSONData]);

  const title = `Peta ${ucFirst(tipeQuerySearch)} - Desa ${ucFirst(
    desa?.nama_desa
  )}`;

  return (
    <LayoutPeta title={title}>
      <LayoutPeta.LeftSidebar isOpen={false}>
        <TabPeta>
          <div className="pl-2 pr-2 pt-2">
            {loadingInfoLayer ? (
              <Spinner className="d-block mx-auto mt-3" />
            ) : infoLayer ? (
              <div>
                <h4 className="text-dark">Info Layer</h4>
                <hr />
                <div className="pb-5 mb-5" style={{ height: "550px" }}>
                  <PerfectScrollbar>
                    <FormGroup>
                      <Label>NBT</Label>
                      <Input
                        readOnly
                        value={
                          activeLayer?.feature?.properties?.properties?.nama
                        }
                      />
                    </FormGroup>

                    {desa?.hierarki?.subdesa && (
                      <FormGroup>
                        <Label>{ucFirst(desa?.hierarki?.jenis_subdesa)}</Label>
                        <Input
                          readOnly
                          value={ucFirst(
                            activeLayer?.feature?.properties?.subdesa
                          )}
                        />
                      </FormGroup>
                    )}

                    {desa?.hierarki?.subsubdesa && (
                      <FormGroup>
                        <Label>
                          {ucFirst(desa?.hierarki?.jenis_subsubdesa)}
                        </Label>
                        <Input
                          readOnly
                          value={ucFirst(
                            activeLayer?.feature?.properties?.subsubdesa
                          )}
                        />
                      </FormGroup>
                    )}

                    {desa?.hierarki?.rw && (
                      <FormGroup>
                        <Label>RW</Label>
                        <Input
                          readOnly
                          value={ucFirst(activeLayer?.feature?.properties?.rw)}
                        />
                      </FormGroup>
                    )}

                    {desa?.hierarki?.rt && (
                      <FormGroup>
                        <Label>RT</Label>
                        <Input
                          readOnly
                          value={ucFirst(activeLayer?.feature?.properties?.rt)}
                        />
                      </FormGroup>
                    )}
                  </PerfectScrollbar>
                </div>
              </div>
            ) : (
              <div>
                <i className="mdi mdi-information-outline mr-1" />
                silakan pilih layer untuk melihat data.
              </div>
            )}
          </div>
        </TabPeta>
      </LayoutPeta.LeftSidebar>

      <MapsCore dynamicDownloadMaps ref={mapRef}>
        {loadingGeoJSONData ? (
          <LoadingGlobal />
        ) : (
          <GeoJSON
            ref={layerRef}
            data={geoJSONData}
            style={createFeatureStyle}
            onEachFeature={handleEachFeature}
          />
        )}
      </MapsCore>
    </LayoutPeta>
  );
};

export default PetaWithLayerBidang;

PetaWithLayerBidang.propTypes = {
  filterURL: PropTypes.string,
};
